import { useState, useRef, useEffect } from 'react';
import format from 'date-fns/format';

import getLogger from '../../util/logger';
import useWebRTCStream from '../useWebRTCStream';
import { VIDEO_RECORDING_ERRORS } from './constants';
import { getErrorString } from '../../util/normalize-errors';

const logger = getLogger('useVideoRecording');

/**
 * Hook to handle & manage all video recording using WebRTC
 *
 * @param {Object} opts List of options
 * @param {String} opts.storeKey Store key for recording camera permissions.
 * @param {String} opts.onError Method to report errors to
 * @param {Function} opts.maxRecordingTime The maximum recoding time for the video.
 * @param {Function} opts.filename Base filename for the video file. Must not include extensions.
 * @param {Function} opts.intervalValue How often will the interval be run.
 * @returns {{ onStreamInit: Function, onDeviceChange: Function, loadingDevices: Boolean, getCameraPermission: Function, cameraStreamElementRef: React.Ref, recording: Boolean, onRecordingStart: Function, onRecordingStop: Function, recordedVideoData: React.Ref, remainingTime: Number, onRecordingReset: Function, isVideoRecorded: Boolean }}
 */
const useVideoRecording = ({
  storeKey,
  onError,
  maxRecordingTime = 30000 /* 30s */,
  filename = 'recorded-video',
  intervalValue = 1000,
}) => {
  const webRTCStream = useWebRTCStream({
    storeKey,
    onError,
  });

  const [recording, setRecording] = useState(false);
  const [remainingTime, setRemainingTime] = useState(maxRecordingTime);
  const [isVideoRecorded, setIsVideoRecorded] = useState(false);

  const timer = useRef(null);
  const countdown = useRef(null);
  const mediaRecorder = useRef(null);
  const recordedVideoData = useRef({
    url: null,
    file: null,
  });

  const { cameraStream } = webRTCStream;

  const stopTracks = () => {
    if (!cameraStream.current) {
      return;
    }

    cameraStream.current.getTracks().forEach(track => track.stop());
  };

  const getSupportedMimeTypes = () => {
    // Didn't add codecs or anything here because technically vimeo supports
    // basically anything. So long as Vimeo cis alright, we're good.
    const possibleTypes = ['video/mp4', 'video/mpeg', 'video/webm'];

    return possibleTypes.filter(mimeType => {
      return MediaRecorder.isTypeSupported(mimeType);
    });
  };

  const onRecordingStop = () => {
    setRecording(false);
    clearTimeout(timer.current);
    clearInterval(countdown.current);
    countdown.current = null;
    timer.current = null;

    if (mediaRecorder.current?.state === 'recording') {
      mediaRecorder.current?.stop();
    }
  };

  const onRecordingStart = () => {
    const recordedBlobs = [];
    const mimeType = getSupportedMimeTypes()[0];

    if (!mimeType) {
      logger.log(VIDEO_RECORDING_ERRORS.NO_MIMETYPE_SUPPORTED);
      onError(new Error(VIDEO_RECORDING_ERRORS.NO_MIMETYPE_SUPPORTED));
      return;
    }

    const options = { mimeType };

    try {
      // Safeguard in case there exists a Recorder.
      onRecordingStop();

      mediaRecorder.current = new MediaRecorder(cameraStream.current, options);

      mediaRecorder.current.onstart = () => {
        timer.current = setTimeout(() => {
          onRecordingStop();
        }, maxRecordingTime);

        countdown.current = setInterval(() => {
          setRemainingTime(
            remainingTimeCurrent => remainingTimeCurrent - intervalValue,
          );
        }, intervalValue);

        setRemainingTime(maxRecordingTime);
        setRecording(true);
      };

      mediaRecorder.current.onstop = () => {
        const recordedVideoBlob = new Blob(recordedBlobs, { type: mimeType });
        recordedVideoData.current = {
          url: window.URL.createObjectURL(recordedVideoBlob),
          file: new File(
            [recordedVideoBlob],
            `${filename}-${format(new Date(), 'yyyy-MM-dd hh:mm')}.webm`,
            { type: recordedVideoBlob.type },
          ),
        };
        setIsVideoRecorded(true);
        setRecording(false);
      };

      mediaRecorder.current.ondataavailable = event => {
        if ((event.data?.size ?? 0) > 0) {
          recordedBlobs.push(event.data);
        }
      };

      mediaRecorder.current.start();
    } catch (error) {
      logger.log(error);
      onError(getErrorString(error));
    }
  };

  const onRecordingReset = () => {
    recordedVideoData.current = {
      url: null,
      file: null,
    };
    onRecordingStop();
    // Fully resets the recorder.
    setIsVideoRecorded(false);
  };

  useEffect(() => {
    return () => {
      stopTracks();
    };
  }, []);

  return {
    ...webRTCStream,
    recording,
    onRecordingStart,
    onRecordingStop,
    remainingTime,
    recordedVideoData,
    onRecordingReset,
    isVideoRecorded,
  };
};

export default useVideoRecording;
